import type { ReactNode } from 'react';
import { useLocalStorage, useViewport } from '@meterup/atto';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SidebarTreeIDProp } from './SidebarTree';

type SidebarTreeCollapsedProp = boolean;

interface SidebarContextData {
  openGroupId: string | null;
  setOpenGroupId: (id: string | null) => void;
  id: SidebarTreeIDProp;
  collapsible: boolean;
  collapsed: SidebarTreeCollapsedProp;
  setCollapsed: (value: SidebarTreeCollapsedProp) => void;
}

interface SidebarGlobalContextData {
  minimized: boolean;
  setMinimized: (minimized: boolean) => void;
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
  /** Application chrome is in the narrow (mobile/tablet) layout. */
  isNarrow: boolean;
  openGroupId: string | null;
  setOpenGroupId: (id: string | null) => void;
  /**
   * Registers a collapsible tree's collapsed setter so that its state can be
   * broadcast to every tree at once (e.g. shift+clicking a tree's toggle).
   * Returns an unregister function.
   */
  registerCollapsibleTree: (
    id: SidebarTreeIDProp,
    setCollapsed: (value: SidebarTreeCollapsedProp) => void,
  ) => () => void;
  /** Sets the collapsed state of every registered collapsible tree. */
  setAllTreesCollapsed: (value: SidebarTreeCollapsedProp) => void;
}

const SidebarGlobalContext = createContext<SidebarGlobalContextData>({
  minimized: false,
  setMinimized: () => {},
  menuOpen: false,
  setMenuOpen: () => {},
  isNarrow: false,
  openGroupId: null,
  setOpenGroupId: () => {},
  registerCollapsibleTree: () => () => {},
  setAllTreesCollapsed: () => {},
});

const SidebarTreeContext = createContext<SidebarContextData>({
  id: '',
  collapsible: false,
  collapsed: false,
  setCollapsed: () => {},
  openGroupId: null,
  setOpenGroupId: () => {},
});

export function SidebarProvider({ children }: { children: ReactNode }) {
  const { breakpoint } = useViewport();
  const isNarrow = breakpoint === 'mobile' || breakpoint === 'tablet';
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const [minimized, setMinimized] = useLocalStorage('dashboard.dock.minimized', false);
  const [menuOpen, setMenuOpenState] = useState(false);

  const collapsibleTreeSettersRef = useRef<
    Map<SidebarTreeIDProp, (value: SidebarTreeCollapsedProp) => void>
  >(new Map());

  const registerCollapsibleTree = useCallback(
    (id: SidebarTreeIDProp, setCollapsed: (value: SidebarTreeCollapsedProp) => void) => {
      collapsibleTreeSettersRef.current.set(id, setCollapsed);
      return () => {
        collapsibleTreeSettersRef.current.delete(id);
      };
    },
    [],
  );

  const setAllTreesCollapsed = useCallback((value: SidebarTreeCollapsedProp) => {
    collapsibleTreeSettersRef.current.forEach((setCollapsed) => setCollapsed(value));
  }, []);

  useEffect(() => {
    if (!isNarrow) {
      setMenuOpenState(false);
    }
  }, [isNarrow]);

  const setMenuOpen = useCallback(
    (value: boolean) => {
      if (!isNarrow) return;
      setMenuOpenState(value);
    },
    [isNarrow],
  );

  const value = useMemo(
    () => ({
      minimized: isNarrow ? false : minimized,
      setMinimized,
      menuOpen,
      setMenuOpen,
      isNarrow,
      openGroupId,
      setOpenGroupId,
      registerCollapsibleTree,
      setAllTreesCollapsed,
    }),
    [
      isNarrow,
      minimized,
      setMinimized,
      menuOpen,
      setMenuOpen,
      openGroupId,
      setOpenGroupId,
      registerCollapsibleTree,
      setAllTreesCollapsed,
    ],
  );

  return <SidebarGlobalContext.Provider value={value}>{children}</SidebarGlobalContext.Provider>;
}

export function useSidebarContext() {
  return useContext(SidebarGlobalContext);
}

const SIDEBAR_TREE_COLLAPSED_STORAGE_PREFIX = 'dashboard.sidebar.tree.collapsed';

// Group-specific provider that combines global state with group-specific state
export function SidebarTreesProvider({
  children,
  collapsible,
  id,
  collapsedState,
  setCollapsedState,
}: {
  collapsible?: boolean;
  children?: ReactNode;
  id?: SidebarTreeIDProp;
  collapsedState?: boolean;
  setCollapsedState?: React.Dispatch<boolean>;
}) {
  const { isNarrow } = useSidebarContext();
  const globalContext = useContext(SidebarGlobalContext);

  const persistKey = id ? `${SIDEBAR_TREE_COLLAPSED_STORAGE_PREFIX}.${id}` : '';
  const [persistedCollapsed, setPersistedCollapsed] = useLocalStorage<boolean>(
    persistKey,
    !!isNarrow,
  );
  const [ephemeralCollapsed, setEphemeralCollapsed] = useState(() => collapsedState ?? !!isNarrow);

  const usePersisted = !!id && collapsedState === undefined && setCollapsedState === undefined;
  const innerCollapsed = usePersisted ? persistedCollapsed : ephemeralCollapsed;
  const setInnerCollapsed = usePersisted ? setPersistedCollapsed : setEphemeralCollapsed;

  const setCollapsed = setCollapsedState ?? setInnerCollapsed;

  const { registerCollapsibleTree } = globalContext;

  useEffect(() => {
    if (!collapsible || !id) return undefined;
    return registerCollapsibleTree(id, setCollapsed);
  }, [collapsible, id, registerCollapsibleTree, setCollapsed]);

  const value = useMemo(
    () => ({
      collapsible: collapsible || false,
      id: id || '',
      collapsed: collapsedState ?? innerCollapsed,
      setCollapsed,
      openGroupId: globalContext.openGroupId,
      setOpenGroupId: globalContext.setOpenGroupId,
    }),
    [collapsible, collapsedState, id, setCollapsed, globalContext, innerCollapsed],
  );

  return <SidebarTreeContext.Provider value={value}>{children}</SidebarTreeContext.Provider>;
}

export function useSidebarTreesContext() {
  const groupContext = useContext(SidebarTreeContext);
  const globalContext = useContext(SidebarGlobalContext);

  // If we're not in a SidebarTreesProvider (group context has empty id),
  // merge the global context state with the group context defaults
  if (!groupContext.id) {
    return {
      ...groupContext,
      openGroupId: globalContext.openGroupId,
      setOpenGroupId: globalContext.setOpenGroupId,
    };
  }

  return groupContext;
}
